/** @jsx jsx */
import React, { useEffect } from 'react';
import { jsx } from '@compiled/react';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';
import IconButton from '@atlaskit/button/icon/button';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import AdControlDropdownMenu from '@atlassian/ad-control-toolkit/ad-control-dropdown-menu/main';

import { adControlsDropDownEntryPoint } from '../dropdown/adControlsDropDownEntryPoint.entrypoint.tsx';

import type { ReasonsModalConfig } from '../dropdown/types.tsx';
import type { AnalyticsConfig } from './types.tsx';

const adControls = defineMessage({
	id: 'jira-adcontrols.button.ad-controls',
	defaultMessage: 'Ad controls',
	description: 'Label for the ad controls dropdown',
});

export const AdControlsButton = ({
	dismiss,
	itemId,
	analytics,
	productName,
	reasonsModalConfig,
}: {
	dismiss: () => Promise<void>;
	itemId: string;
	analytics: AnalyticsConfig;
	productName: string;
	reasonsModalConfig?: ReasonsModalConfig;
}): JSX.Element => {
	const { formatMessage } = useIntl();

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		adControlsDropDownEntryPoint,
		{},
	);

	useEffect(() => {
		return () => {
			entryPointActions.unload();
		};
	}, [entryPointActions]);

	return (
		<AdControlDropdownMenu
			itemId={itemId}
			placement="bottom-end"
			trigger={({ triggerRef, onClick, ...props }) => (
				<IconButton
					{...props}
					appearance="subtle"
					spacing="compact"
					icon={ShowMoreHorizontalIcon}
					label={formatMessage(adControls)}
					ref={triggerRef}
					onClick={(e: React.MouseEvent) => {
						e.stopPropagation();
						entryPointActions.load();
						onClick?.(e);
					}}
				/>
			)}
		>
			{(props) => (
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id={`${itemId}-dropdown`}
					runtimeProps={{
						...props,
						onDismiss: dismiss,
						itemId,
						analytics,
						productName,
						reasonsModalConfig,
					}}
				/>
			)}
		</AdControlDropdownMenu>
	);
};
